import { jsx as _jsx } from "react/jsx-runtime";
import { DragOutlined, SettingOutlined, SortAscendingOutlined } from "@ant-design/icons";
import { useLayoutEffect, useMemo, useRef } from "react";
import { Icon } from "@devowl-wp/react-folder-tree";
import { __, _i } from "../../utils/i18n.js";
import { rmlOpts } from "../../utils/rmlOpts.js";
function useAppTreeUiBundle({ toolbarActiveButton, creatableButtons, lockedVisible, lockedToolTipText, orderDisabled, renameDisabled, trashDisabled, sortDisabled, detailsDisabled, renderOrderMenu, renderSortMenu, handleOrderClick, handleOrderCancel, handleReload, handleTrashModifier, handleMultipleTrashModifier, handleDetails, selectedFolderType, onUiBundleChange }) {
    const toolbarBackLabelText = toolbarActiveButton ? __("Back") : __("Cancel");
    const showOrderToolbar = rmlOpts.others.isPro || rmlOpts.others.showProHints;
    const uiBundle = useMemo(()=>({
            headline: /*#__PURE__*/ _jsx("span", {
                style: {
                    paddingRight: 5
                },
                children: __("Folders")
            }),
            renameSaveText: /*#__PURE__*/ _jsx(Icon, {
                type: "save"
            }),
            renameAddText: /*#__PURE__*/ _jsx(Icon, {
                type: "save"
            }),
            noFoldersTitle: __("No folders found"),
            noFoldersDescription: __("No folders have been created yet. Just click on the button above to create your first folder."),
            noSearchResult: __("No search results."),
            isBusyHeader: false,
            creatable: {
                backButton: {
                    cssClasses: "page-title-action add-new-h2",
                    label: __("Cancel")
                },
                buttons: creatableButtons
            },
            toolbar: {
                backButton: {
                    label: toolbarActiveButton === "order" || toolbarActiveButton === "sort" ? toolbarBackLabelText : __("Cancel"),
                    save: __("Save")
                },
                buttons: {
                    ...+rmlOpts.others.userSettings ? {
                        usersettings: {
                            content: /*#__PURE__*/ _jsx(SettingOutlined, {}),
                            toolTipTitle: __("Settings"),
                            toolTipText: __("General settings for the current logged in user."),
                            onClick: ()=>handleDetails("usersettings")
                        }
                    } : {},
                    locked: {
                        content: /*#__PURE__*/ _jsx(Icon, {
                            type: "lock"
                        }),
                        visible: lockedVisible,
                        toolTipTitle: __("Permissions"),
                        toolTipText: lockedToolTipText
                    },
                    ...showOrderToolbar ? {
                        order: {
                            content: /*#__PURE__*/ _jsx(DragOutlined, {}),
                            toolTipTitle: __("Reorder files in this folder"),
                            toolTipText: _i(__("Start to reorder the files / images by {{strong}}title, filename, ID, ...{{/strong}}"), {
                                strong: /*#__PURE__*/ _jsx("strong", {})
                            }),
                            onClick: handleOrderClick,
                            onCancel: handleOrderCancel,
                            menu: renderOrderMenu,
                            toolTipPlacement: "topLeft",
                            dropdownPlacement: "bottomLeft",
                            disabled: orderDisabled
                        }
                    } : {},
                    reload: {
                        content: /*#__PURE__*/ _jsx(Icon, {
                            type: "reload"
                        }),
                        toolTipTitle: __("Refresh"),
                        toolTipText: __("Refreshes the current folder view."),
                        onClick: handleReload
                    },
                    rename: {
                        action: "rename",
                        content: /*#__PURE__*/ _jsx(Icon, {
                            type: "edit"
                        }),
                        toolTipTitle: __("Rename"),
                        toolTipText: __("Rename the current selected folder."),
                        disabled: renameDisabled
                    },
                    trash: {
                        content: /*#__PURE__*/ _jsx(Icon, {
                            type: "delete"
                        }),
                        toolTipTitle: __("Delete"),
                        toolTipText: __("Delete the current selected folder."),
                        modifier: handleTrashModifier,
                        disabled: trashDisabled
                    },
                    sort: {
                        action: "sort",
                        content: /*#__PURE__*/ _jsx(SortAscendingOutlined, {}),
                        toolTipTitle: __("Rearrange"),
                        toolTipText: __("Change the hierarchical order of the folders."),
                        menu: renderSortMenu,
                        toolTipPlacement: "topLeft",
                        dropdownPlacement: "bottomLeft",
                        disabled: sortDisabled
                    },
                    details: {
                        content: /*#__PURE__*/ _jsx(Icon, {
                            type: "ellipsis"
                        }),
                        disabled: detailsDisabled,
                        toolTipTitle: __("Folder details"),
                        toolTipText: __("Select a folder and view more details about it."),
                        onClick: ()=>handleDetails("details")
                    }
                }
            },
            multiToolbar: {
                backButton: {
                    label: toolbarBackLabelText,
                    save: __("Save")
                },
                buttons: {
                    trash: {
                        content: /*#__PURE__*/ _jsx(Icon, {
                            type: "delete"
                        }),
                        toolTipTitle: __("Delete"),
                        toolTipText: __("Delete the current selected folders."),
                        modifier: handleMultipleTrashModifier
                    }
                }
            }
        }), [
        creatableButtons,
        detailsDisabled,
        handleDetails,
        handleMultipleTrashModifier,
        handleOrderCancel,
        handleOrderClick,
        handleReload,
        handleTrashModifier,
        lockedToolTipText,
        lockedVisible,
        orderDisabled,
        renameDisabled,
        renderOrderMenu,
        renderSortMenu,
        showOrderToolbar,
        sortDisabled,
        toolbarActiveButton,
        toolbarBackLabelText
    ]);
    const uiBundlePushKey = useMemo(()=>JSON.stringify({
            toolbarActiveButton,
            lockedVisible,
            orderDisabled,
            renameDisabled,
            trashDisabled,
            sortDisabled,
            detailsDisabled,
            showOrderToolbar,
            creatableKeys: Object.keys(creatableButtons),
            selectedCreatableType: selectedFolderType
        }), [
        toolbarActiveButton,
        lockedVisible,
        orderDisabled,
        renameDisabled,
        trashDisabled,
        sortDisabled,
        detailsDisabled,
        showOrderToolbar,
        creatableButtons,
        selectedFolderType
    ]);
    const uiBundlePushKeyRef = useRef(null);
    useLayoutEffect(()=>{
        if (uiBundlePushKeyRef.current === uiBundlePushKey) {
            return;
        }
        uiBundlePushKeyRef.current = uiBundlePushKey;
        onUiBundleChange(uiBundle);
    }, [
        onUiBundleChange,
        uiBundle,
        uiBundlePushKey
    ]);
    return uiBundle;
}
export { useAppTreeUiBundle };
